import { Box, VStack } from "@chakra-ui/react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { DocViewerContext } from "../state";
import { resetState, setNumPages, setZoomLevel } from "../state/actions";
import { calculateOptimalZoom } from "../state/utils";
import { DocRenderer } from "./DocRenderer";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

export const PdfRenderer: DocRenderer = ({
  documentData,
  showAllPages = false,
}) => {
  const { state, dispatch } = useContext(DocViewerContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [pdfWidth, setPdfWidth] = useState<number>(0);
  const [pdfHeight, setPdfHeight] = useState<number>(0);

  /*
    This is a workaround to prevent the PDF error while re-rendering the component.
    Reference: https://github.com/wojtekmaj/react-pdf/issues/1539#issuecomment-2032572723
  */
  const fileSlice = useMemo(() => documentData?.slice(0), [documentData]);

  // Update container dimensions on resize
  useEffect(() => {
    const updateContainerDimensions = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
        setContainerHeight(containerRef.current.offsetHeight);
      }
    };

    // Initial measurement
    updateContainerDimensions();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(updateContainerDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Update zoom level when container dimensions or PDF dimensions change
  useEffect(() => {
    if (
      pdfWidth > 0 &&
      pdfHeight > 0 &&
      containerWidth > 0 &&
      containerHeight > 0
    ) {
      const optimalZoom = calculateOptimalZoom(
        pdfWidth,
        pdfHeight,
        containerWidth,
        containerHeight
      );
      dispatch(setZoomLevel(optimalZoom));
    }
  }, [containerHeight, containerWidth, dispatch, pdfHeight, pdfWidth]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    dispatch(resetState());
    dispatch(setNumPages(numPages));
  };

  const onPageLoadSuccess = (page: {
    originalWidth?: number;
    originalHeight?: number;
    width: number;
    height: number;
  }) => {
    const width = page.originalWidth ?? page.width;
    const height = page.originalHeight ?? page.height;
    setPdfWidth(width);
    setPdfHeight(height);
  };

  return (
    <Box
      ref={containerRef}
      width="100%"
      height="100%"
      overflow="auto"
      display="flex"
      alignItems="safe center"
      justifyContent="safe center"
    >
      <Document
        loading="Loading..."
        file={fileSlice}
        onLoadSuccess={onDocumentLoadSuccess}
      >
        {showAllPages ? (
          <VStack spacing={4} align="center">
            {Array.from(new Array(state.numPages), (el, index) => (
              <Page
                key={`page_${index + 1}`}
                loading="Loading..."
                renderTextLayer={true}
                renderAnnotationLayer={false}
                scale={state.zoomLevel}
                pageNumber={index + 1}
                rotate={state.rotation}
                onLoadSuccess={onPageLoadSuccess}
              />
            ))}
          </VStack>
        ) : (
          <Page
            loading="Loading..."
            renderTextLayer={true}
            renderAnnotationLayer={false}
            scale={state.zoomLevel}
            pageNumber={state.currentPage}
            rotate={state.rotation}
            onLoadSuccess={onPageLoadSuccess}
          />
        )}
      </Document>
    </Box>
  );
};
